import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useBooking } from "@/context/BookingContext";
import { useSettings } from "@/context/SettingsContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/packages", label: "Party Packages" },
  { to: "/events", label: "Events" },
  { to: "/summer-camps", label: "Summer Camps" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { openBooking } = useBooking();
  const { settings } = useSettings();
  const phone = settings.contact.phone;

  // Lock background scroll while the mobile menu is open (no scroll-trap)
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="navbar"
      className="fixed top-0 inset-x-0 z-50 bg-[#0A0A0A]/85 backdrop-blur-md border-b border-rgv-maroon"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center py-2">
          <img
            src="/assets/logos/rgv-gaming-logo.png"
            alt="RGV Gaming"
            className="h-14 sm:h-16 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `font-display uppercase tracking-wide text-lg italic transition-colors py-2 ${
                  isActive ? "text-rgv-red" : "text-white hover:text-rgv-red"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${phone}`}
            data-testid="nav-phone"
            className="flex items-center gap-2 text-rgv-gray hover:text-white transition-colors py-2"
          >
            <Phone className="h-4 w-4 text-rgv-red" />
            <span className="text-sm font-medium">{phone}</span>
          </a>
          <button
            onClick={() => openBooking()}
            data-testid="nav-book-btn"
            className="btn-skew btn-red px-6 py-2.5"
          >
            <span>Book Now</span>
          </button>
        </div>

        <button
          className="lg:hidden text-white p-2 -mr-2 flex items-center justify-center min-h-[44px] min-w-[44px]"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#0A0A0A] border-t border-rgv-maroon px-4 py-4 space-y-1 max-h-[calc(100vh-5rem)] overflow-y-auto"
        >
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center min-h-[48px] font-display uppercase italic text-2xl ${
                  isActive ? "text-rgv-red" : "text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={`tel:${phone}`}
            className="flex items-center gap-2 text-rgv-gray min-h-[48px] pt-2 border-t border-rgv-maroon mt-2"
          >
            <Phone className="h-4 w-4 text-rgv-red" /> {phone}
          </a>
          <button
            onClick={() => { setOpen(false); openBooking(); }}
            data-testid="mobile-menu-book-btn"
            className="btn-skew btn-red w-full py-3 mt-2"
          >
            <span>Book Now</span>
          </button>
        </div>
      )}
    </header>
  );
}
